// Pendampingan (admin console, POC): every "Minta pendampingan konsultan"
// request from Penyedia, newest first, with a status filter. Per request:
// the topic, the paket project, the Penyedia, the stage, the status badge,
// a Konsultan select (the active konsultan accounts made in Akun; choosing
// one assigns it and moves a Baru request to Ditugaskan), a Status select,
// and the message with the notes thread behind a disclosure.
import { useId, useMemo, useState, type ReactNode } from "react";
import * as API from "../api";
import type { Consultation, ConsultationStatus, Konsultan } from "../api";
import { formatCount, formatDate } from "../lib/format";
import { ConsultationNotes, ConsultationStatusLine } from "../project/PaketParts";
import { CONSULT_STATUS_LABEL, STAGE_LABEL } from "../project/stages";
import { EmptyState, Field, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";

const STATUSES: ConsultationStatus[] = ["baru", "ditugaskan", "selesai"];

export function PendampinganAdminScreen() {
  useTitle("Pendampingan");
  const list = useAsync<Consultation[]>(() => API.listAllConsultations(), []);
  const consultants = useAsync<Konsultan[]>(() => API.listKonsultan(), []);
  const [filter, setFilter] = useState<ConsultationStatus | "">("");
  const rows = list.data ?? [];
  const counts = useMemo(() => {
    const out: Record<ConsultationStatus, number> = { baru: 0, ditugaskan: 0, selesai: 0 };
    for (const r of rows) out[r.status]++;
    return out;
  }, [rows]);
  const shown = filter ? rows.filter((r) => r.status === filter) : rows;
  const updated = (c: Consultation) => list.set(rows.map((r) => (r.id === c.id ? c : r)));

  let body: ReactNode;
  if (list.loading && !list.data) body = <SkeletonRows count={4} label="Memuat permintaan" />;
  else if (list.error && !list.data) {
    body = (
      <Notice
        tone="danger"
        title="Permintaan belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={list.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(list.error)}</p>
      </Notice>
    );
  } else if (rows.length === 0) {
    body = <EmptyState icon="messages" title="Belum ada permintaan" body="Permintaan pendampingan dari Penyedia tampil di sini untuk ditugaskan ke konsultan." />;
  } else if (shown.length === 0) {
    body = <p className="pd-empty">Tidak ada permintaan dengan status ini.</p>;
  } else {
    body = (
      <ul className="pd-rows" aria-label="Permintaan pendampingan">
        {shown.map((c) => (
          <AdminRequest key={c.id} consultation={c} consultants={consultants.data ?? []} consultantsLoading={consultants.loading} onUpdated={updated} />
        ))}
      </ul>
    );
  }

  return (
    <div className="pd-page">
      <PageHeader title="Pendampingan" lead="Permintaan pendampingan konsultan dari Penyedia. Tugaskan ke akun konsultan; buat akun konsultan di Akun." />
      {consultants.data && consultants.data.length === 0 ? (
        <Notice tone="warning" title="Belum ada akun konsultan">
          <p>
            Buat akun dengan peran Konsultan di <a href="/admin/akun">Akun</a> agar permintaan dapat ditugaskan.
          </p>
        </Notice>
      ) : null}
      {rows.length > 0 ? (
        <div className="filter-chips" role="group" aria-label="Saring menurut status">
          <button type="button" className="filter-chip" aria-pressed={filter === ""} onClick={() => setFilter("")}>
            <span>Semua</span>
            <span className="filter-count num">{formatCount(rows.length)}</span>
          </button>
          {STATUSES.map((s) => (
            <button key={s} type="button" className="filter-chip" aria-pressed={filter === s} onClick={() => setFilter(filter === s ? "" : s)}>
              <span>{CONSULT_STATUS_LABEL[s]}</span>
              <span className="filter-count num">{formatCount(counts[s])}</span>
            </button>
          ))}
        </div>
      ) : null}
      {body}
    </div>
  );
}

interface AdminRequestProps {
  consultation: Consultation;
  consultants: Konsultan[];
  consultantsLoading: boolean;
  onUpdated: (c: Consultation) => void;
}

function AdminRequest({ consultation: c, consultants, consultantsLoading, onUpdated }: AdminRequestProps) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (patch: API.ConsultationAssign) => {
    setBusy(true);
    setError(null);
    try {
      onUpdated(await API.assignConsultation(c.id, patch));
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // A consultant assigned earlier whose account is no longer active still shows by name.
  const known = consultants.some((k) => k.id === c.consultantId);
  return (
    <li className="pd-row pd-admin-row">
      <div className="pd-row-top">
        <h2 className="pd-row-title">{c.topic}</h2>
        <ConsultationStatusLine consultation={c} />
      </div>
      <p className="pd-meta">
        {c.projectName}, {c.requesterName}, tahap {STAGE_LABEL[c.stage]}, diminta {formatDate(c.createdAt)}
      </p>
      <div className="pd-admin-controls">
        <Field id={`${id}-k`} label="Konsultan">
          <select
            id={`${id}-k`}
            value={c.consultantId ?? ""}
            disabled={busy || consultantsLoading}
            aria-busy={busy || undefined}
            onChange={(e) => void save({ consultantId: e.currentTarget.value || null })}
          >
            <option value="">Belum ditugaskan</option>
            {c.consultantId && !known ? <option value={c.consultantId}>{c.consultantName ?? "Konsultan"}</option> : null}
            {consultants.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name} ({k.email})
              </option>
            ))}
          </select>
        </Field>
        <Field id={`${id}-s`} label="Status">
          <select id={`${id}-s`} value={c.status} disabled={busy} onChange={(e) => void save({ status: e.currentTarget.value as ConsultationStatus })}>
            {STATUSES.map((s) => (
              <option key={s} value={s} disabled={s === "ditugaskan" && !c.consultantId}>
                {CONSULT_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {error ? (
        <p className="pk-item-error" role="alert">
          <Glyph name="danger" size={16} />
          <span>{error}</span>
        </p>
      ) : null}
      <details className="pd-details">
        <summary>
          <Glyph name="messages" size={16} />
          <span>Pesan dan catatan ({c.notes.length})</span>
        </summary>
        <div>
          <p className="pd-message">{c.message}</p>
          <ConsultationNotes consultation={c} viewer="konsultan" />
        </div>
      </details>
    </li>
  );
}
